import React, { useState, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { type TranscriptCue, srtTimecodeToSeconds, secondsToSrtTimecode } from '@bynder-studio/render-core';
import { Button, token, textStyles, Dropdown, Flex, Box, notify, Tooltip } from '@bynder/design-system';
import { IconClear, IconDownload, IconMore, IconSparkles, IconUpload } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { parseSrt, cuesToSrt } from './parseSrt';
import { classifySrtContent, fixSrtContent } from './lintSrt';
import TranscriptCueItem from './TranscriptCueItem';
import features from 'packages/configs/features';
import useAccessRights from 'packages/hooks/useAccessRights';
import MaybeTooltip from 'packages/common/MaybeTooltip';

type TranscriptFormProps = {
    cues: TranscriptCue[];
    syncToken?: number;
    isGenerating: boolean;
    designName?: string;
    pageName?: string;
    onGenerate: () => void;
    onCuesChange: (cues: TranscriptCue[]) => void;
    onSeekToCue?: (cue: TranscriptCue) => void;
    isDisabled?: boolean;
};

const sanitizeFileNamePart = (value: string): string =>
    value
        .trim()
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_');

const TranscriptForm = ({
    cues,
    syncToken = 0,
    isGenerating,
    designName = '',
    pageName = '',
    onGenerate,
    onCuesChange,
    onSeekToCue,
    isDisabled = false,
}: TranscriptFormProps) => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const [editingCueId, setEditingCueId] = useState<string | null>(null);
    // Keep the latest cues in a ref so per-cue handlers stay referentially stable across keystrokes,
    // letting React.memo skip re-rendering every other cue while one is being edited.
    const cuesRef = useRef(cues);

    const isAITranscriptFeatureEnabled = useMemo(
        () => isPlatformAllowed([features.AI_VIDEO_TRANSCRIBE]),
        [isPlatformAllowed],
    );

    cuesRef.current = cues;

    const handleCueUpdate = useCallback(
        (updatedCue: TranscriptCue) => {
            onCuesChange(cuesRef.current.map((c) => (c.id === updatedCue.id ? updatedCue : c)));
        },
        [onCuesChange],
    );

    const handleCueDelete = useCallback(
        (id: string) => {
            setEditingCueId((current) => (current === id ? null : current));
            onCuesChange(cuesRef.current.filter((c) => c.id !== id));
        },
        [onCuesChange],
    );

    const handleCueInsertAfter = useCallback(
        (afterId: string) => {
            const current = cuesRef.current;
            const afterIndex = current.findIndex((c) => c.id === afterId);

            if (afterIndex === -1) {
                return;
            }

            const anchor = current[afterIndex];
            const newId = `srt-cue-new-${Date.now()}`;
            // New cue starts where the anchor ends and lasts 3 seconds by default.
            const newCue: TranscriptCue = {
                id: newId,
                startTime: anchor.endTime,
                endTime: secondsToSrtTimecode(srtTimecodeToSeconds(anchor.endTime) + 3),
                text: '',
            };

            onCuesChange([...current.slice(0, afterIndex + 1), newCue, ...current.slice(afterIndex + 1)]);
            setEditingCueId(newId);
        },
        [onCuesChange],
    );

    const handleCueInsertBefore = useCallback(
        (beforeId: string) => {
            const current = cuesRef.current;
            const beforeIndex = current.findIndex((c) => c.id === beforeId);

            if (beforeIndex === -1) {
                return;
            }

            const anchor = current[beforeIndex];
            const newId = `srt-cue-new-${Date.now()}`;
            // New cue ends where the anchor begins and lasts up to 3 seconds by default.
            const newStartSecs = Math.max(0, srtTimecodeToSeconds(anchor.startTime) - 3);
            const newCue: TranscriptCue = {
                id: newId,
                startTime: secondsToSrtTimecode(newStartSecs),
                endTime: anchor.startTime,
                text: '',
            };

            onCuesChange([...current.slice(0, beforeIndex), newCue, ...current.slice(beforeIndex)]);
            setEditingCueId(newId);
        },
        [onCuesChange],
    );

    const handleStartEdit = useCallback((id: string) => {
        setEditingCueId(id);
    }, []);

    const handleEndEdit = useCallback((id: string) => {
        setEditingCueId((current) => (current === id ? null : current));
    }, []);

    const handleClearAll = useCallback(() => {
        setEditingCueId(null);
        onCuesChange([]);
    }, [onCuesChange]);

    const handleUpload = useCallback(() => {
        const input = document.createElement('input');

        input.type = 'file';
        input.accept = '.srt';

        input.onchange = () => {
            const file = input.files?.[0];

            if (!file) {
                return;
            }

            const reader = new FileReader();

            reader.onload = (e) => {
                const content = e.target?.result;
                const status = classifySrtContent(content);

                // The status guarantees a string, but narrow explicitly for FileReader's union result.
                if (status === 'invalid' || typeof content !== 'string') {
                    notify({
                        title: translate('editor.transcript.upload.invalid.title'),
                        description: translate('editor.transcript.upload.invalid.description'),
                        variant: 'error',
                    });

                    return;
                }

                const normalizedContent = status === 'fixable' ? fixSrtContent(content) : content;

                onCuesChange(parseSrt(normalizedContent));
            };

            reader.onerror = () => {
                notify({
                    title: translate('editor.transcript.upload.invalid.title'),
                    description: translate('editor.transcript.upload.invalid.description'),
                    variant: 'error',
                });
            };

            reader.readAsText(file);
        };

        input.click();
    }, [onCuesChange, translate]);

    const handleDownload = useCallback(() => {
        if (!cues.length) {
            return;
        }

        const srtContent = cuesToSrt(cues);
        const safeDesignName = sanitizeFileNamePart(designName) || 'design';
        const safePageName = sanitizeFileNamePart(pageName) || 'page';
        const blob = new Blob([srtContent], { type: 'text/plain;charset=utf-8' });
        const downloadUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');

        anchor.href = downloadUrl;
        anchor.download = `${safeDesignName}_${safePageName}.srt`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(downloadUrl);
    }, [cues, designName, pageName]);

    return (
        <Container>
            <Flex gap="3">
                <GenerateButtonWrapper>
                    <MaybeTooltip
                        hasTooltip={!isAITranscriptFeatureEnabled}
                        content={translate('editor.transcript.generate-button.tooltip')}
                    >
                        <Button
                            icon={<IconSparkles />}
                            variant="primary"
                            onClick={onGenerate}
                            isDisabled={isGenerating || isDisabled || !isAITranscriptFeatureEnabled}
                            isFullWidth
                        >
                            {isGenerating
                                ? translate('editor.transcript.generate-button.generating')
                                : translate('editor.transcript.generate-button')}
                        </Button>
                    </MaybeTooltip>
                </GenerateButtonWrapper>
                <Dropdown
                    trigger={({ isOpen, ...triggerProps }) => (
                        <Button
                            icon={<IconMore />}
                            variant="clean"
                            isDisabled={isGenerating}
                            title=""
                            {...triggerProps}
                        />
                    )}
                >
                    <Dropdown.Item onClick={handleUpload} icon={<IconUpload />}>
                        {translate('editor.transcript.dropdown.upload')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleDownload} icon={<IconDownload />} isDisabled={cues.length === 0}>
                        {translate('editor.transcript.dropdown.download')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleClearAll} icon={<IconClear />} isDisabled={cues.length === 0}>
                        {translate('editor.transcript.dropdown.clear')}
                    </Dropdown.Item>
                </Dropdown>
            </Flex>
            {cues.length === 0 && (
                <Box paddingTop="6" paddingBottom="3">
                    <Copy>{translate('editor.transcript.description')}</Copy>
                </Box>
            )}
            <CueList>
                {cues.length > 0 &&
                    cues.map((cue, i) => (
                        <React.Fragment key={cue.id}>
                            <TranscriptCueItem
                                cue={cue}
                                syncToken={syncToken}
                                index={i + 1}
                                isEditing={editingCueId === cue.id}
                                isLast={i === cues.length - 1}
                                onStartEdit={handleStartEdit}
                                onEndEdit={handleEndEdit}
                                onUpdate={handleCueUpdate}
                                onDelete={handleCueDelete}
                                onInsertBefore={handleCueInsertBefore}
                                onInsertAfter={handleCueInsertAfter}
                                onSeek={onSeekToCue}
                            />
                        </React.Fragment>
                    ))}
            </CueList>
        </Container>
    );
};

const Copy = styled.p`
    ${textStyles.uiTextS};
    margin-bottom: 0;
`;

const CueList = styled.div`
    padding: ${token.spacing2} 0;
`;

const Container = styled.div`
    margin-bottom: ${token.spacing3};
`;

const GenerateButtonWrapper = styled.div`
    flex: 1;
`;

export default TranscriptForm;
